import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  Building2,
  Car,
  ClipboardCheck,
  CreditCard,
  Fuel,
  Gauge,
  History,
  ShieldCheck,
  LifeBuoy,
  Settings,
  ShieldAlert,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/store';
import { BrandLogo } from '@/components/brand-logo';
import { cn } from '@/lib/utils';

type Role = 'admin' | 'manager' | 'driver' | 'workshop';

export interface NavItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  soon?: boolean;
  /** Se definido, só usuários com um desses papéis veem o item. */
  roles?: Role[];
}

export const NAV_ITEMS: Array<{ section?: string; items: NavItem[] }> = [
  {
    items: [{ label: 'Visão geral', to: '/app', icon: Gauge }],
  },
  {
    section: 'Frota',
    items: [
      { label: 'Veículos', to: '/app/vehicles', icon: Car },
      { label: 'Motoristas', to: '/app/drivers', icon: Users },
      { label: 'Fornecedores', to: '/app/suppliers', icon: Building2 },
    ],
  },
  {
    section: 'Operação',
    items: [
      { label: 'Checklists', to: '/app/checklists', icon: ClipboardCheck },
      { label: 'Abastecimento', to: '/app/fueling', icon: Fuel },
      { label: 'Manutenção', to: '/app/maintenance', icon: Wrench },
      { label: 'Multas', to: '/app/fines', icon: ShieldAlert },
    ],
  },
  {
    section: 'Administração',
    items: [
      { label: 'Relatórios', to: '/app/reports', icon: BarChart3 },
      { label: 'Usuários', to: '/app/users', icon: UserCog },
      { label: 'Notificações', to: '/app/notifications', icon: Bell },
      {
        label: 'Auditoria',
        to: '/app/audit',
        icon: History,
        roles: ['admin', 'manager'],
      },
      {
        label: 'Dados e LGPD',
        to: '/app/lgpd',
        icon: ShieldCheck,
        roles: ['admin'],
      },
      { label: 'Cobrança', to: '/app/billing', icon: CreditCard, roles: ['admin'] },
      { label: 'Configurações', to: '/app/settings', icon: Settings },
      { label: 'Ajuda', to: '/app/help', icon: LifeBuoy },
    ],
  },
];

interface SidebarProps {
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const role = useAuthStore((s) => s.user?.role) as Role | undefined;

  return (
    <aside className="flex h-full w-full flex-col bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))]">
      <div className="flex h-16 items-center gap-3 border-b border-[hsl(var(--sidebar-border))] px-5">
        <BrandLogo variant="inverted" />
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5">
        {NAV_ITEMS.map((group, groupIdx) => {
          const visibleItems = group.items.filter(
            (item) => !item.roles || (role && item.roles.includes(role)),
          );
          if (visibleItems.length === 0) return null;
          return (
          <div key={groupIdx} className="space-y-1">
            {group.section && (
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--sidebar-foreground)/0.5)]">
                {group.section}
              </div>
            )}
            {visibleItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/app'}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      'hover:bg-[hsl(var(--sidebar-accent))] hover:text-white',
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-white shadow-[inset_2px_0_0_0_hsl(158_64%_55%)]'
                        : 'text-[hsl(var(--sidebar-foreground)/0.75)]',
                    )
                  }
                >
                  <Icon className="size-4 flex-shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {item.soon && (
                    <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--sidebar-foreground)/0.7)]">
                      Em breve
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      <div className="border-t border-[hsl(var(--sidebar-border))] px-5 py-3 text-xs text-[hsl(var(--sidebar-foreground)/0.5)]">
        v0.1.0 · beta
      </div>
    </aside>
  );
}
